import { useMemo } from 'react';

const BEND = { cylinder: .06, cone: .1 };
export const glassShapeOf = product => (product?.kind !== 'textile' && product?.category === 'glas') ? (product.glass_shape || 'flat') : null;
export const isCurvedGlass = product => ['cylinder', 'cone'].includes(glassShapeOf(product));
const taperOf = (product, shape) => shape === 'cone' ? (product.glass_top_mm && product.glass_bottom_mm ? Math.min(1, product.glass_bottom_mm / product.glass_top_mm) : .8) : 1;

// Geometry of the visible glass half: alpha = half angle of the engraving zone, rs = screen radius (svg units).
export const glassGeometry = (area, product) => {
  const shape = glassShapeOf(product), radiusMm = (product.glass_top_mm || product.width_mm || product.area_width_mm) / 2;
  const alpha = Math.min(Math.PI / 2 * .97, product.area_width_mm / (2 * radiusMm)), rs = (area.w / 2) / Math.sin(alpha), bend = (BEND[shape] || 0) * area.h;
  const cx = area.x + area.w / 2, pad = bend / Math.max(.05, 1 - Math.cos(alpha)) + 6;
  return { shape, alpha, rs, bend, cx, taper: taperOf(product, shape), box: { x: cx - rs, y: area.y - pad, w: 2 * rs, h: area.h + 2 * pad }, maxShift: area.w / 2 + (Math.PI / 2 / alpha) * area.w / 2 };
};

// Displacement map (R = dx, G = dy) + fade (B) projecting the flat design onto the visible half of a cylinder/cone.
const buildMap = (area, g) => {
  const { box } = g, W = Math.round(Math.min(1000, box.w * 2)), H = Math.round(Math.min(900, box.h * 2));
  const cosA = Math.cos(g.alpha), dx = new Float32Array(W * H), dy = new Float32Array(W * H), fade = new Float32Array(W * H);
  let max = 1;
  for (let j = 0; j < H; j += 1) {
    const y = box.y + j / (H - 1) * box.h, t = Math.min(1, Math.max(0, (y - area.y) / area.h)), f = 1 - (1 - g.taper) * t;
    for (let i = 0; i < W; i += 1) {
      const k = j * W + i, x = box.x + i / (W - 1) * box.w, u = (x - g.cx) / (g.rs * f);
      if (Math.abs(u) >= 1) continue;
      const psi = Math.asin(u), c = Math.cos(psi), flatX = g.cx + (psi / g.alpha) * (area.w / 2);
      dx[k] = flatX - x; dy[k] = g.bend * (1 - c) / Math.max(.05, 1 - cosA); fade[k] = Math.max(.12, Math.pow(c, .9));
      max = Math.max(max, Math.abs(dx[k]), Math.abs(dy[k]));
    }
  }
  const scale = 2 * max + 2, canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d'), img = ctx.createImageData(W, H);
  for (let k = 0; k < W * H; k += 1) {
    img.data[k * 4] = Math.round((dx[k] / scale + .5) * 255); img.data[k * 4 + 1] = Math.round((dy[k] / scale + .5) * 255);
    img.data[k * 4 + 2] = Math.round(fade[k] * 255); img.data[k * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return { href: canvas.toDataURL('image/png'), scale };
};

// Frosted engraving look for glass (white matte frost, grain, soft glow, subtle cut shadow); optional curvature warp.
export const GlassFilter = ({ id, area, product, warp }) => {
  const shape = glassShapeOf(product), curved = warp && isCurvedGlass(product), g = curved ? glassGeometry(area, product) : null;
  const map = useMemo(() => g ? buildMap(area, g) : null, [curved, area.x, area.y, area.w, area.h, product.width_mm, product.area_width_mm, product.glass_top_mm, product.glass_bottom_mm, shape]); // eslint-disable-line react-hooks/exhaustive-deps
  const box = g ? { x: g.box.x, y: g.box.y, width: g.box.w, height: g.box.h } : { x: area.x - 10, y: area.y - 20, width: area.w + 20, height: area.h + 40 };
  return <filter id={id} filterUnits="userSpaceOnUse" primitiveUnits="userSpaceOnUse" {...box} colorInterpolationFilters="sRGB" data-testid="glass-filter" data-glass-shape={shape} data-glass-warp={curved ? 'on' : 'off'}>
    {map && <><feImage href={map.href} x={g.box.x} y={g.box.y} width={g.box.w} height={g.box.h} preserveAspectRatio="none" result="map" />
      <feDisplacementMap in="SourceGraphic" in2="map" scale={map.scale} xChannelSelector="R" yChannelSelector="G" result="art" /></>}
    <feColorMatrix in={map ? 'art' : 'SourceGraphic'} type="matrix" values="0 0 0 0 .95  0 0 0 0 .97  0 0 0 0 .97  0 0 0 1 0" result="shape" />
    <feTurbulence type="fractalNoise" baseFrequency="1.15" numOctaves="2" seed="7" result="noise" />
    <feColorMatrix in="noise" type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 -1.1 1.4" result="grain" />
    <feComposite in="grain" in2="shape" operator="in" result="frost" />
    <feGaussianBlur in="shape" stdDeviation="1.4" result="blur" />
    <feComponentTransfer in="blur" result="glow"><feFuncA type="linear" slope=".5" /></feComponentTransfer>
    <feOffset in="shape" dx=".5" dy=".7" result="offset" />
    <feColorMatrix in="offset" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 .3 0" result="shadow" />
    <feMerge result="look"><feMergeNode in="shadow" /><feMergeNode in="glow" /><feMergeNode in="frost" /></feMerge>
    {map && <><feColorMatrix in="map" type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 1 0 0" result="fade" />
      <feComposite in="look" in2="fade" operator="arithmetic" k1="1" k2="0" k3="0" k4="0" /></>}
  </filter>;
};
